import { useEffect, useRef, useState } from 'react';
import type * as PdfJs from 'pdfjs-dist';
import type { PDFDocumentLoadingTask, PDFDocumentProxy, RenderTask } from 'pdfjs-dist';

/**
 * pdf.js draws the page on a canvas itself, so a PDF looks the same in every
 * browser and in the desktop app's webview. The library and its worker are
 * loaded on first use, so pages without a PDF never pay for them.
 */
async function loadPdfJs(): Promise<typeof PdfJs> {
  const [pdfjs, worker] = await Promise.all([
    import('pdfjs-dist'),
    import('pdfjs-dist/build/pdf.worker.min.mjs?url'),
  ]);
  pdfjs.GlobalWorkerOptions.workerSrc = worker.default;
  return pdfjs;
}

export type PdfState =
  { status: 'loading' } | { status: 'failed' } | { status: 'ready'; document: PDFDocumentProxy };

/** Open the PDF at `url` (nothing for null); it is released when the caller goes away. */
export function usePdf(url: string | null): PdfState {
  const [state, setState] = useState<PdfState>({ status: 'loading' });

  useEffect(() => {
    if (url === null) return;
    let cancelled = false;
    let loading: PDFDocumentLoadingTask | null = null;
    setState({ status: 'loading' });
    void loadPdfJs()
      .then((pdfjs) => {
        if (cancelled) return null;
        loading = pdfjs.getDocument({ url, withCredentials: true });
        return loading.promise;
      })
      .then((document) => {
        if (!cancelled && document) setState({ status: 'ready', document });
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'failed' });
      });
    return () => {
      cancelled = true;
      void loading?.destroy();
    };
  }, [url]);

  return state;
}

/**
 * One page of the document. `width` is the size it is drawn at in CSS pixels,
 * at the screen's pixel ratio so text stays sharp. `fluid` lets CSS scale the
 * canvas to its container instead.
 */
export function PdfPage({
  document,
  pageNumber,
  width,
  fluid = false,
  label,
  className,
}: {
  document: PDFDocumentProxy;
  pageNumber: number;
  width: number;
  fluid?: boolean;
  label: string;
  className?: string;
}): React.ReactElement {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    let task: RenderTask | null = null;
    let cancelled = false;
    void document
      .getPage(pageNumber)
      .then((page) => {
        const canvas = canvasRef.current;
        if (cancelled || !canvas) return;
        const base = page.getViewport({ scale: 1 });
        const ratio = window.devicePixelRatio || 1;
        const viewport = page.getViewport({ scale: (width / base.width) * ratio });
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        task = page.render({ canvas, viewport });
        return task.promise;
      })
      // A render cancelled because the page changed, or a document closed
      // while it was drawing, is not an error worth showing.
      .catch(() => undefined);
    return () => {
      cancelled = true;
      task?.cancel();
    };
  }, [document, pageNumber, width]);

  return (
    <canvas
      ref={canvasRef}
      className={className}
      role="img"
      aria-label={label}
      style={{ width: fluid ? '100%' : `${width}px`, height: 'auto' }}
    />
  );
}
